import { Icon } from "materia"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import type { PeerProfile } from "../../types"
import { PeerAvatar, PeerAvatarGroup } from "../PeerAvatar"
import { conversationTimestamp } from "./conversationTimestamp"
import s from "./ConversationRow.module.scss"

type ConversationRowProps = {
  /** A group's name, or the person's, already resolved. */
  name: string
  /**
   * Who to show: one person for a direct conversation, the first few members
   * for a group. Length decides which avatar the row gets.
   */
  faces: PeerProfile[]
  /** The last message's body, or what stood in for it. */
  preview: string
  /** Unix ms of the last message. */
  timestamp: number
  isUnread: boolean
  /** The user's Mute: the bell-off icon takes the unread dot's place. */
  isMuted: boolean
  isActive: boolean
  onOpen: () => void
}

export function ConversationRow({
  name,
  faces,
  preview,
  timestamp,
  isUnread,
  isMuted,
  isActive,
  onOpen,
}: ConversationRowProps) {
  const { t } = useTranslation()

  return (
    <button
      className={modifiers(s, "row", { isUnread, isActive })}
      type="button"
      onClick={onOpen}
    >
      {faces.length > 1 ? (
        <PeerAvatarGroup members={faces} size="small" />
      ) : (
        <PeerAvatar
          displayName={name}
          avatar={faces[0]?.avatar ?? null}
          size="medium"
        />
      )}

      <span className={s.row__text}>
        <p className={s.row__name}>{name}</p>
        <p className={s.row__preview}>{preview}</p>
      </span>

      <span className={s.row__meta}>
        <time
          className={s.row__time}
          dateTime={new Date(timestamp).toISOString()}
        >
          {conversationTimestamp(timestamp)}
        </time>
        {isMuted ? (
          <Icon
            icon="BellOff"
            size="small"
            title={String(t("chat.MUTED"))}
            className={s.row__muted}
          />
        ) : (
          <span className={s.row__unreadDot} />
        )}
      </span>
    </button>
  )
}
